import { io } from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class WebSocketService {
  constructor() {
    this.socket = null;
    this.currentSessionId = null;
    this.isConnected = false;
    this.connectionPromise = null;
  }

  connect() {
    if (this.socket && this.isConnected) {
      return Promise.resolve(this.socket);
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve) => {
      if (!this.socket) {
        // Force polling for Safari and other browsers with strict policies
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        
        this.socket = io(BACKEND_URL, {
          path: '/api/socket.io',  // Use /api/socket.io path for Kubernetes ingress routing
          transports: isSafari ? ['polling', 'websocket'] : ['websocket', 'polling'],
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionAttempts: 10,
          reconnectionDelayMax: 5000,
          timeout: 20000,
          forceNew: false,
          upgrade: true,
        });

        this.socket.on('connect', () => {
          console.log('✓ WebSocket connected');
          this.isConnected = true;
          this.connectionPromise = null;
          resolve(this.socket);
        });

        this.socket.on('disconnect', (reason) => {
          console.log('✗ WebSocket disconnected:', reason);
          this.isConnected = false;
        });

        this.socket.on('connect_error', (error) => {
          console.error('✗ WebSocket connection error:', error.message);
        });

        this.socket.on('reconnect', (attemptNumber) => {
          console.log(`✓ WebSocket reconnected after ${attemptNumber} attempts`);
          this.isConnected = true;
          // Re-join session if we have one
          if (this.currentSessionId) {
            this.socket.emit('join_session', { session_id: this.currentSessionId });
            console.log(`↻ Re-joined session: ${this.currentSessionId}`);
          }
        });

        this.socket.on('reconnect_attempt', (attemptNumber) => {
          console.log(`↻ Reconnection attempt ${attemptNumber}...`);
        });

        this.socket.on('reconnect_error', (error) => {
          console.error('✗ Reconnection error:', error.message);
        });

        this.socket.on('reconnect_failed', () => {
          console.error('✗ Reconnection failed');
          this.isConnected = false;
        });
      } else {
        resolve(this.socket);
      }
    });

    return this.connectionPromise;
  }

  async disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.currentSessionId = null;
      this.isConnected = false;
      this.connectionPromise = null;
    }
  }

  async joinSession(sessionId) {
    await this.connect();
    this.currentSessionId = sessionId;
    
    if (this.socket && this.isConnected) {
      this.socket.emit('join_session', { session_id: sessionId });
      console.log(`→ Joined session: ${sessionId}`);
    } else {
      console.error('Cannot join session - socket not connected');
    }
  }

  leaveSession(sessionId) {
    if (this.socket && sessionId) {
      this.socket.emit('leave_session', { session_id: sessionId });
      this.currentSessionId = null;
      console.log(`← Left session: ${sessionId}`);
    }
  }

  // Event listeners
  onSlideChange(callback) {
    if (this.socket) {
      this.socket.on('slide_change', callback);
    }
  }

  onLiveStatusChange(callback) {
    if (this.socket) {
      this.socket.on('live_status_change', callback);
    }
  }

  onDepthNotesUpdate(callback) {
    if (this.socket) {
      this.socket.on('depth_notes_update', callback);
    }
  }

  onSessionState(callback) {
    if (this.socket) {
      this.socket.on('session_state', callback);
    }
  }

  // Event emitters
  emitDepthNotesUpdate(sessionId, slideId, depthNotes) {
    if (this.socket && this.isConnected) {
      this.socket.emit('depth_notes_update', {
        session_id: sessionId,
        slide_id: slideId,
        depth_notes: depthNotes,
      });
    }
  }

  // Remove listeners
  removeListener(event) {
    if (this.socket) {
      this.socket.off(event);
    }
  }

  // Get connection status
  isSocketConnected() {
    return this.socket && this.isConnected;
  }
}

const websocketService = new WebSocketService();
export default websocketService;
